// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

import { useEffect, useState } from 'react'
import { Bell, Check, RefreshCw } from 'lucide-react'
import * as api from '../../api/client'

const EVENT_LABELS: Record<string, { label: string; desc: string }> = {
  new_rx: { label: 'New prescription', desc: 'When a new prescription is received for your pharmacy.' },
  new_comment: { label: 'New comment', desc: 'When someone adds a comment to a prescription you can access.' },
  determination_ready: { label: 'Determination ready', desc: 'When a clinical determination is ready for review.' },
  ingest_issue: { label: 'Ingest issue', desc: 'When a data-ingest problem affects your pharmacy.' },
  shipment_update: { label: 'Shipment update', desc: 'When a shipment or tracking status changes.' },
  secure_share_document: { label: 'Secure share', desc: 'When a document is shared with you securely.' },
  // CareRx (review W2-B): offered only to staff of a CareRx pharmacy. Adverse events and new clinical
  // holds are not here — they are safety signals every CareRx staff member receives.
  'carerx.cr.assigned': { label: 'CareRx: review assigned to you', desc: 'When a clinical review is assigned to you.' },
  'carerx.hold.released': { label: 'CareRx: hold released', desc: 'When a clinical hold is released and the therapy resumes.' },
  'carerx.billing_review': { label: 'CareRx: billing review', desc: 'When a fill is blocked because its claim was rejected.' },
  'carerx.staffing.two_misses': { label: 'CareRx: two missed visits', desc: 'When a therapy has two missed visits.' },
  carerx_escalation: { label: 'CareRx: escalations', desc: 'When an exception or an invoice passes its deadline.' },
  carerx_cancel_eligible: { label: 'CareRx: cancel-eligible exceptions', desc: 'Daily, when exceptions have been escalated for 14+ days.' },
  carerx_credential_expiry: { label: 'CareRx: nurse credentials expiring', desc: 'Daily, at 60 / 30 / 7 days before a required credential expires.' },
  carerx_timeout: { label: 'CareRx: renewals due', desc: 'When therapy renewals reach their interval.' },
  carerx_visit_day_before: { label: 'CareRx: visit tomorrow', desc: 'The day before a confirmed visit.' },
}

/**
 * Per-user notification preferences. Each user controls which events notify them and through which
 * channel. Email delivery is live (outbox + ACS/SMTP); the Email toggles are enabled when the
 * preferences endpoint reports email_available=true.
 */
export default function NotificationSettings() {
  const [prefs, setPrefs] = useState<api.NotificationPref[]>([])
  const [emailAvailable, setEmailAvailable] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => { void load() }, [])
  async function load() {
    setLoading(true); setErr(null)
    try { const r = await api.getNotificationPrefs(); setPrefs(r.preferences); setEmailAvailable(r.email_available) }
    catch (e: any) { setErr(e?.message || 'Failed to load preferences') }
    finally { setLoading(false) }
  }
  function toggle(et: string, field: 'inapp_enabled' | 'email_enabled') {
    setPrefs(ps => ps.map(p => p.event_type === et ? { ...p, [field]: !p[field] } : p)); setSaved(false)
  }
  async function save() {
    setSaving(true); setErr(null)
    try { await api.putNotificationPrefs(prefs); setSaved(true); setTimeout(() => setSaved(false), 2500) }
    catch (e: any) { setErr(e?.message || 'Failed to save') }
    finally { setSaving(false) }
  }

  return (
    <div style={{ maxWidth: 680, margin: '0 auto', padding: '8px 0' }}>
      <h1 style={h1}><Bell size={20} /> Notification settings</h1>
      <p style={sub}>Choose which events notify you. These settings apply only to your account.</p>

      {err && <div style={errBox}>{err}</div>}

      {loading ? (
        <div style={loadingBox}><RefreshCw size={18} style={{ animation: 'spin 1s linear infinite' }} /> Loading…</div>
      ) : (
        <>
          <div style={card}>
            <div style={tableHead}>
              <span style={{ flex: 1 }}>Event</span>
              <span style={colHead}>In-app</span>
              <span style={colHead}>Email</span>
            </div>
            {prefs.map(p => {
              const meta = EVENT_LABELS[p.event_type] || { label: p.event_type, desc: '' }
              return (
                <div key={p.event_type} style={prefRow}>
                  <div style={{ flex: 1 }}>
                    <div style={evtLabel}>{meta.label}</div>
                    <div style={evtDesc}>{meta.desc}</div>
                  </div>
                  <div style={colCell}>
                    <Toggle on={p.inapp_enabled} onClick={() => toggle(p.event_type, 'inapp_enabled')} />
                  </div>
                  <div style={colCell}>
                    <Toggle on={p.email_enabled} disabled={!emailAvailable} onClick={() => toggle(p.event_type, 'email_enabled')} />
                  </div>
                </div>
              )
            })}
          </div>

          {!emailAvailable && (
            <p style={note}>Email delivery is coming soon. In-app notifications are active now.</p>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16 }}>
            <button onClick={save} disabled={saving} style={saveBtn}>
              {saving ? <RefreshCw size={15} style={{ animation: 'spin 1s linear infinite' }} /> : <Check size={15} />}
              {saving ? 'Saving…' : 'Save preferences'}
            </button>
            {saved && <span style={{ color: '#059669', fontSize: 13, fontWeight: 600 }}>Saved ✓</span>}
          </div>
        </>
      )}
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  )
}

function Toggle({ on, onClick, disabled }: { on: boolean; onClick: () => void; disabled?: boolean }) {
  return (
    <button onClick={disabled ? undefined : onClick} disabled={disabled} aria-pressed={on}
            style={{ ...track, ...(on ? trackOn : null), ...(disabled ? trackDisabled : null) }}>
      <span style={{ ...knob, transform: on ? 'translateX(18px)' : 'translateX(0)' }} />
    </button>
  )
}

const h1: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 9, fontSize: 22, fontWeight: 800, color: '#0f172a', margin: 0 }
const sub: React.CSSProperties = { fontSize: 14, color: '#64748b', margin: '6px 0 20px' }
const errBox: React.CSSProperties = { background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', padding: 12, borderRadius: 10, marginBottom: 16, fontSize: 13 }
const loadingBox: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 10, padding: 40, justifyContent: 'center', color: '#64748b' }
const card: React.CSSProperties = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' }
const tableHead: React.CSSProperties = { display: 'flex', alignItems: 'center', padding: '10px 16px', borderBottom: '2px solid #e2e8f0', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, color: '#94a3b8', fontWeight: 700 }
const colHead: React.CSSProperties = { width: 64, textAlign: 'center' }
const prefRow: React.CSSProperties = { display: 'flex', alignItems: 'center', padding: '14px 16px', borderBottom: '1px solid #f1f5f9' }
const evtLabel: React.CSSProperties = { fontSize: 14, fontWeight: 600, color: '#1e293b' }
const evtDesc: React.CSSProperties = { fontSize: 12.5, color: '#94a3b8', marginTop: 2, lineHeight: 1.4 }
const colCell: React.CSSProperties = { width: 64, display: 'flex', justifyContent: 'center' }
const track: React.CSSProperties = { position: 'relative', width: 38, height: 20, borderRadius: 999, border: 'none', background: '#cbd5e1', cursor: 'pointer', padding: 2, transition: 'background .15s' }
const trackOn: React.CSSProperties = { background: '#0891b2' }
const trackDisabled: React.CSSProperties = { opacity: 0.4, cursor: 'not-allowed' }
const knob: React.CSSProperties = { display: 'block', width: 16, height: 16, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 2px rgba(0,0,0,0.2)', transition: 'transform .15s' }
const note: React.CSSProperties = { fontSize: 12.5, color: '#94a3b8', marginTop: 12 }
const saveBtn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 14, fontWeight: 600, padding: '10px 18px', borderRadius: 8, border: 'none', background: '#0891b2', color: '#fff', cursor: 'pointer' }
